import {ChangeEvent, useContext, useEffect, useMemo, useState} from "react";
import IGetLotsDTO from "../../../models/DTO/IGetLotsDTO";
import IOnGetLotsDTO from "../../../models/DTO/IOnGetLotsDTO";
import IOnProcessLotDTO from "../../../models/DTO/IOnProcessLotDTO";
import IProcessLotDTO from "../../../models/DTO/IProcessLotDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import ILot from "../../../models/ILot";
import ILotUI from "../../../models/ILotUI";
import IOwTableCols from "../../../models/IOwTableCols";
import UserService from "../../../services/UserService";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTable from "../../shared/OwTable/OwTable";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./Lots.scss";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import CreateLotSidebar from "../CreateLotSidebar/CreateLotSidebar";
import * as _ from "lodash-es";
import UiContext from "../../../contexts/UiContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import {SocketContext} from "../../../contexts/SocketContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import {ConfirmDeleteOverlayFn} from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import {OwDeleteEditActions} from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import UIService from "../../../services/UIService";
import {SearchSection} from "../../shared/SearchSection/SearchSection";

type Props = {};

export const Lots = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const renderActionCol = (row: ILotUI, index: number) => {
        return (
            <div>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        confirmDeleteRow(row, index);
                    }}
                    onEditClickFn={() => {
                        openSidebar(row);
                    }}
                />
            </div>
        );
    };

    const renderExpDateFn = (dateStr: string) => {
        if (dateStr) {
            return UserService.parseDate(dateStr);
        } else {
            return null;
        }
    };

    const getTableCols = (): IOwTableCols[] => {
        const tableCols: IOwTableCols[] = [
            {
                isActionsCol: true,
                title: "",
                valueProp: "",
                renderFn: renderActionCol,
                renderFnEmitsRow: true,
            },
            {
                title: "sku",
                valueProp: "Catalog_Item",
                valueSubProp: "sku",
            },
            {
                title: "name",
                valueProp: "name",
            },
            {
                title: "qty",
                valueProp: "qty",
            },
        ];

        if (supplierModel.has_exp_date) {
            tableCols.push({
                title: "exp_date",
                valueProp: "exp_date",
                renderFn: renderExpDateFn,
            });
        }

        return tableCols;
    }

    const [columns, setColumns] = useState(getTableCols());
    const crumbs = useMemo(() => {
        return [
            { title: "inventory", url: "/inventory" },
            { title: "lots", url: "/lots" },
         ];
    }, []);
    const [lots, setLots] = useState<ILot[]>([]);
    const [totalLots, setTotalLots] = useState(0);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [initialData, setInitialData] = useState(true);
    const [limit, setLimit] = useState(25);
    const [offset, setOffset] = useState(0);
    const [name, setName] = useState("");
    const [sku, setSku] = useState("");
    const [deleteElem, setDeleteElem] = useState<string | null>(null);
    const [deleteRow, setDeleteRow] = useState<ILot | null>(null);

    const onGetLots = (data: IOnGetLotsDTO) => {
        setIsDataFetching(false);
        setInitialData(false);
        setLots(data.lots);
        setTotalLots(data.totalLots);
    };

    const onProcessLot = (data: IOnProcessLotDTO) => {
        const notification: INotification = {
            message: "successfully_processed_lot",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc
        }

        uiContext.notifyFn(null, notification);

        setDeleteElem(null);
        setDeleteRow(null);

        getLots();
    };

    const sockets: IUiSocketSetup[] = [
        {endpoint: EndpointsEnum.CatalogItemOnGetLots, fn: onGetLots},
        {endpoint: EndpointsEnum.CatalogItemOnProcessLot, fn: onProcessLot},
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getLots();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);



    const getLots = (queryString?: string) => {
        const payload: IGetLotsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString,
            limit: limit,
            offset: offset,
        };

        socket.emit(EndpointsEnum.CatalogItemGetLots, payload);
    };

    const cancel = (refresh?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refresh) {
            getLots();
        }
    };

    const openSidebar = (row?: ILotUI) => {
        const component = (
            <CreateLotSidebar
                userModel={userModel}
                supplierModel={supplierModel}
                socket={socket}
                cancel={cancel}
                catalogItemModel={row?.Catalog_Item}
                lot={row}
            />
        );

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const confirmDeleteRow = (row: ILotUI, index: number) => {
        setDeleteElem(`row-${index}`);
        setDeleteRow(row);
    };

    const performSearch = () => {
        const query: string[] = [];
        let queryString = "";

        if (!_.isNil(name)) {
            query.push(`name=${name}`);
        }

        if (!_.isNil(sku)) {
            query.push(`sku=${sku}`);
        }

        if (query.length) {
            queryString = "?";

            queryString += query.join("&");
        }

        getLots(queryString);
    };

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "sku":
                setSku(value);
                break;

            case "name":
                setName(value);
                break;
        }
    };

    const resetSearch = () => {
        setSku("");
        setName("");
        setOffset(0);
        setLimit(25);
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter") {
            performSearch();
        }
    };

    const addLot = () => {
        openSidebar();
    };

    const cancelConfirmDeleteFn = () => {
        setDeleteRow(null);
        setDeleteElem(null);
    };

    const commitDeleteFn = () => {
        if (deleteRow) {
            const payload: IProcessLotDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                lot: deleteRow,
                deleteFlag: true,
            };

            socket.emit(EndpointsEnum.CatalogItemProcessLot, payload);

            setDeleteRow(null);
            setDeleteElem(null);
        }
    };

    return (
        <div className="LotsOverview">
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className="col-12 d-flex align-items-center justify-items-center">
                    <OwTitle title={"lots"} leftIconName={"fad fa-ball-pile"} className={"me-2"} />
                    <OwButton title={"add"} className={"btn-warning"} leftIconClass={"fas fa-plus"} fn={addLot} />
                </div>
            </div>

            <div className={"mt-2 mb-2"}>
                <SearchSection>
                    <div className="d-flex">
                        <div className={"me-2"}>
                            <OwLabel title={"name"} />
                            <OwInputWrapper name={"name"} fn={handleChange} value={name} onKeyDownFn={enterSearch} />
                        </div>

                        <div>
                            <OwLabel title={"sku"} />
                            <div className={"d-flex"}>
                                <OwInputWrapper name={"sku"} fn={handleChange} value={sku} onKeyDownFn={enterSearch} />
                                <div className={"ms-2 d-flex"}>
                                    <div className={"me-2"}>
                                        <OwButton title={"search"} fn={performSearch} />
                                    </div>
                                    <div>
                                        <OwButton title={"reset"} className={"btn-danger"} fn={resetSearch} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </SearchSection>
            </div>

            <div className={"position-relative"}>
                <ConfirmDeleteOverlayFn
                    targetElem={deleteElem}
                    message={"confirm_delete"}
                    handleConfirm={commitDeleteFn}
                    handleCancel={cancelConfirmDeleteFn}
                    nestedParentElem={"lots-table"}
                    skipMainView={true}
                />

                <OwTable
                    id={"lots-table"}
                    columns={columns}
                    dataSet={lots}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={isDataFetching}
                    initialData={initialData}
                    tableConfig={{
                        tableStriped: true,
                    }}
                />
            </div>

            <div>
                <PaginationBar
                    offset={offset}
                    limit={limit}
                    totalRecords={totalLots}
                    onPaginationChanged={onPaginationChanged}
                />
            </div>
        </div>
    );
}
